import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { updateAvatar } from '@/utils/storage';

interface ProfileData {
  first_name: string;
  last_name: string;
  phone_number: string | null;
  timezone: string | null;
  preferred_language: string | null;
  avatar_url: string | null;
}

export const useProfileManager = (userId: string, initialProfile: any) => {
  const { toast } = useToast();
  const [profile, setProfile] = useState(initialProfile);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null);

  const handleAvatarChange = (file: File | null, preview: string | null) => {
    console.log("Setting avatar file in useProfileManager:", file?.name, file?.type);
    setAvatarFile(file);
    setAvatarPreview(preview);
  };

  const refreshProfileData = async () => {
    if (!userId) return;
    
    try {
      const { data, error } = await supabase
        .from('clients')
        .select('*')
        .eq('id', userId)
        .single();
        
      if (error) throw error;
      
      setProfile(data);
      return data;
    } catch (error) {
      console.error('Error refreshing profile:', error);
      throw error;
    }
  };

  const updateProfile = async (values: ProfileData): Promise<boolean> => {
    if (!userId || !profile) return false;
    
    try {
      setIsSaving(true);
      setError(null);
      
      let avatarPath = profile.avatar_url;
      
      // Only upload the avatar if a new file is selected
      if (avatarFile) {
        try {
          console.log("Avatar file being uploaded:", avatarFile);
          
          const { path } = await updateAvatar('client.photo', userId, avatarFile);
          avatarPath = path;
          
        } catch (error: any) {
          console.error('Error updating avatar:', error);
          toast({
            title: "Upload failed",
            description: error.message || "Failed to upload avatar image. Please try again.",
            variant: "destructive",
            duration: 8000,
          });
          return false;
        }
      }
      
      // Update the client record in the database
      const updatePayload: Record<string, any> = {
        first_name: values.first_name,
        last_name: values.last_name,
        phone_number: values.phone_number,
        preferred_language: values.preferred_language,
        avatar_url: avatarPath,
        updated_at: new Date().toISOString(),
      };

      // Only persist timezone when the user explicitly selected one.
      if (typeof values.timezone === 'string' && values.timezone.trim()) {
        updatePayload.timezone = values.timezone.trim();
      }

      const { error: updateError } = await supabase
        .from('clients')
        .update(updatePayload)
        .eq('id', userId);
        
      if (updateError) {
        console.error('Error updating profile in database:', updateError);
        throw updateError;
      }
      
      // Refresh the profile data from the database
      await refreshProfileData();
      
      toast({
        title: "Profile updated",
        description: "Your profile has been successfully updated",
        duration: 8000,
      });
      
      // Clear the avatar file state
      setAvatarFile(null);
      setAvatarPreview(null);
      
      return true;
      
    } catch (error: any) {
      console.error('Error updating profile:', error);
      setError(error.message || "Failed to update profile");
      toast({
        title: "Update failed",
        description: error.message || "Failed to update profile",
        variant: "destructive",
        duration: 8000,
      });
      return false;
    } finally {
      setIsSaving(false);
    }
  };

  return {
    profile,
    setProfile,
    error,
    setError,
    isSaving,
    avatarFile,
    avatarPreview,
    handleAvatarChange,
    refreshProfileData,
    updateProfile
  };
};
